/**
 * Press bar review count.
 *
 * The number paints with the HTML, from the maintainable figure in theme
 * settings, so it is never late and never shifts the bar. Growave remains the
 * source of truth: its ReviewsTrustBadgeWidget is the only place a store-wide
 * total exists — it counts shop-level reviews that no product metafield exposes
 * — so the widget is mounted out of view, and once it resolves every rendered
 * count animates up to the live figure.
 *
 * That indirection is what keeps the bar fast. Growave's bundle URL is written
 * by an inline script near the very end of the document, so the preload scanner
 * never sees it and the request cannot start until the HTML is nearly parsed.
 * Waiting on that chain to paint a number means seconds of empty bar; counting
 * up to it instead costs nothing.
 *
 * The scrolling banner repeats its blocks to fill the marquee, so there are many
 * .pressbar__count nodes showing the same figure. One resolver drives all of
 * them from a single poll and a single animation frame loop.
 *
 * Growave mounts the badge inside an open shadow root, so it cannot be reached
 * with a plain document.querySelector — see findBadge below.
 *
 * Growave renders the count unformatted ("49118"), so it is reformatted here to
 * match the theme ("49,118"). Every count renders in tabular figures, so digits
 * changing during the animation cannot change an element's width.
 *
 * Loaded with `defer`, so it never blocks parsing or rendering.
 */
(function () {
  'use strict';

  if (window.__pressbarReviewCountInit) return;
  window.__pressbarReviewCountInit = true;

  var BADGE = '.gw-rv-trust-badge';
  var GIVE_UP_AFTER_MS = 20000;
  var POLL_INTERVAL_MS = 250;
  var DURATION_MS = 1400;
  var VISIBILITY_TIMEOUT_MS = 4000;
  var SHADOW_MAX_DEPTH = 6;
  var SHADOW_SWEEP_EVERY = 8;

  var targets = document.querySelectorAll('.pressbar__count');
  if (!targets.length) return;

  // Built once and reused. toLocaleString constructs a formatter on every call,
  // which is wasteful across ~80 animation frames.
  var formatter = null;

  function format(value) {
    if (!formatter) {
      try {
        formatter = new Intl.NumberFormat(document.documentElement.lang || 'en');
      } catch (error) {
        formatter = { format: function (n) { return String(n); } };
      }
    }

    return formatter.format(value);
  }

  function toNumber(text) {
    var digits = (text || '').replace(/\D/g, '');
    return digits ? parseInt(digits, 10) : null;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function write(value) {
    var text = format(value);
    for (var i = 0; i < targets.length; i++) targets[i].textContent = text;
  }

  /**
   * Growave renders the count as the badge's first element child, ahead of the
   * "verified reviews" label and the star rating. Rather than bet on that one
   * shape surviving a Growave release, try three readings in order of how
   * specific they are. While the widget is still loading, its own skeleton sits
   * in that slot with no digits in it, so a reading only counts once it has one.
   */
  function readCount(badge) {
    var heading = badge.querySelector('.gw-text--heading-3xl');
    var value = heading && toNumber(heading.textContent);
    if (value) return value;

    var node = badge.firstElementChild;
    while (node) {
      var isLabel = node.classList.contains('gw-rv-trust-badge__verified-text');
      var isRating = node.classList.contains('gw-rv-trust-badge__rating');

      if (!isLabel && !isRating) {
        value = toNumber(node.textContent);
        if (value) return value;
      }

      node = node.nextElementSibling;
    }

    // The label carries no number and the stars expose their rating through
    // aria-label rather than text, so any digits left are the count.
    return toNumber(badge.textContent);
  }

  /**
   * Counts every rendered figure up to Growave's from one frame loop. easeOutExpo
   * covers most of the distance immediately and settles slowly, so the eye
   * follows the last few digits rather than a blur.
   */
  function countUp(from, to) {
    if (from === to) return;

    if (prefersReducedMotion()) return write(to);

    var startedAt = null;

    function frame(now) {
      if (startedAt === null) startedAt = now;

      var progress = Math.min((now - startedAt) / DURATION_MS, 1);
      var eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);

      write(Math.round(from + (to - from) * eased));

      if (progress < 1) window.requestAnimationFrame(frame);
    }

    window.requestAnimationFrame(frame);
  }

  /**
   * Counting up where nobody is looking is just a jump, so wait for a press bar
   * to be on screen.
   *
   * Every copy has to be observed, not just the first. The marquee repeats its
   * content inside an overflow-hidden track and slides it, so most copies —
   * including the first in document order — sit outside that clipped box.
   * IntersectionObserver clips the intersection rect by ancestor overflow, so
   * those copies never report as intersecting and gating on any single one can
   * block the animation forever.
   */
  function whenVisible(run) {
    if (!('IntersectionObserver' in window)) return run();

    var fired = false;

    function fire() {
      if (fired) return;
      fired = true;
      observer.disconnect();
      window.clearTimeout(fallback);
      run();
    }

    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) return fire();
      }
    }, { threshold: 0 });

    for (var i = 0; i < targets.length; i++) observer.observe(targets[i]);

    // A press bar can sit entirely off screen, or inside a track that never
    // exposes a copy. Show the live figure rather than hold the old one.
    var fallback = window.setTimeout(fire, VISIBILITY_TIMEOUT_MS);
  }

  /**
   * Growave mounts the trust badge inside an open shadow root, so an ordinary
   * document.querySelector never sees it. Search in cost order: the light DOM
   * first, then the shadow root we already found, then the subtrees of Growave's
   * own placeholders, and only as a last resort every shadow host on the page.
   *
   * That last sweep is O(every element in the document), so it runs on a slow
   * cadence rather than on every poll.
   */
  var cachedRoot = null;
  var sweepCountdown = 0;

  function searchShadow(el, depth) {
    if (depth > SHADOW_MAX_DEPTH) return null;

    var root = el.shadowRoot;
    if (root) {
      var hit = root.querySelector(BADGE);
      if (hit) {
        cachedRoot = root;
        return hit;
      }

      var inner = root.children;
      for (var i = 0; i < inner.length; i++) {
        var nested = searchShadow(inner[i], depth + 1);
        if (nested) return nested;
      }
    }

    var kids = el.children;
    for (var k = 0; k < kids.length; k++) {
      var found = searchShadow(kids[k], depth + 1);
      if (found) return found;
    }

    return null;
  }

  function findBadge() {
    var badge = document.querySelector(BADGE);
    if (badge) return badge;

    if (cachedRoot) {
      badge = cachedRoot.querySelector(BADGE);
      if (badge) return badge;
    }

    var placeholders = document.querySelectorAll('.gw-rv-trust-badge-widget-placeholder');
    for (var i = 0; i < placeholders.length; i++) {
      badge = searchShadow(placeholders[i], 0);
      if (badge) return badge;
    }

    if (sweepCountdown-- > 0) return null;
    sweepCountdown = SHADOW_SWEEP_EVERY;

    var hosts = document.querySelectorAll('*');
    for (var h = 0; h < hosts.length; h++) {
      if (!hosts[h].shadowRoot) continue;

      badge = hosts[h].shadowRoot.querySelector(BADGE);
      if (badge) {
        cachedRoot = hosts[h].shadowRoot;
        return badge;
      }
    }

    return null;
  }

  function apply() {
    var badge = findBadge();
    if (!badge) return false;

    var live = readCount(badge);
    if (!live) return false;

    var rendered = toNumber(targets[0].textContent);

    for (var i = 0; i < targets.length; i++) targets[i].setAttribute('data-state', 'live');

    if (rendered === null) {
      write(live);
    } else if (rendered !== live) {
      whenVisible(function () { countUp(rendered, live); });
    }

    return true;
  }

  if (!apply()) {
    // Poll rather than observe. Growave mounts through its own framework, and a
    // MutationObserver only sees what happens in the tree it is given — the
    // badge can be moved, re-rendered or attached somewhere unexpected and the
    // observer never fires. A quarter-second querySelector costs nothing and
    // catches the count however it arrives.
    var elapsed = 0;
    var timer = window.setInterval(function () {
      elapsed += POLL_INTERVAL_MS;

      // Growave never answering is a normal outcome — the app can be disabled or
      // the script blocked. The rendered figure simply stands, which is why it
      // is kept maintainable in theme settings.
      if (apply() || elapsed >= GIVE_UP_AFTER_MS) window.clearInterval(timer);
    }, POLL_INTERVAL_MS);
  }

  // Diagnostics only. Growave renders client-side, so when the live count fails
  // to arrive this is the fastest way to see which step broke. The shadow-root
  // walk lives here rather than in the poll: it is O(every element on the page)
  // and there is no evidence Growave needs it.
  window.__pressbarReviewCount = function () {
    sweepCountdown = 0;
    var badge = findBadge();

    return {
      counts: targets.length,
      placeholders: document.querySelectorAll('.gw-rv-trust-badge-widget-placeholder').length,
      scripts: document.querySelectorAll('script[src*="pressbar-review-count"]').length,
      badgeFound: !!badge,
      badgeInShadowRoot: !!cachedRoot,
      badgeHTML: badge ? badge.outerHTML : null,
      liveCount: badge ? readCount(badge) : null,
      rendered: targets[0].textContent.trim(),
      anyVisible: Array.prototype.some.call(targets, function (el) {
        var box = el.getBoundingClientRect();
        return box.width > 0 && box.height > 0 &&
          box.top < window.innerHeight && box.bottom > 0 &&
          box.left < window.innerWidth && box.right > 0;
      }),
      state: targets[0].getAttribute('data-state') || 'settings',
      reducedMotion: prefersReducedMotion()
    };
  };
})();
